import { useCallback, useEffect, useRef, useState } from "react";
import { api, ApiError, getToken } from "../lib/api";
import { useActiveCategories } from "../lib/meta";
import { formatINR, formatPoints, todayInput } from "../lib/format";
import type { Card, ChargeSlipRow, Person, SlipRead } from "../lib/types";
import { Badge, EmptyState, ErrorBanner, PageHeader, Panel, Spinner, StatTile } from "../components/ui";
import { Link } from "react-router-dom";

interface Draft {
  merchant: string;
  amount: string;
  date: string;
  category: string;
  channel: "ONLINE" | "OFFLINE";
  isInternational: boolean;
  cardholderId: string;
  notes: string;
}

export default function SlipsPage() {
  const categories = useActiveCategories();
  const [cards, setCards] = useState<Card[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [cardId, setCardId] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [read, setRead] = useState<SlipRead | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [slips, setSlips] = useState<ChargeSlipRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [showText, setShowText] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const [c, s, p] = await Promise.all([
        api.get<{ cards: Card[] }>("/cards"),
        api.get<{ slips: ChargeSlipRow[] }>("/slips"),
        api.get<{ people: Person[] }>("/people"),
      ]);
      setCards(c.cards);
      setSlips(s.slips);
      setPeople(p.people);
      setCardId((id) => id || c.cards.find((x) => x.isActive)?.id || "");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load your slips");
      setSlips([]);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  function reset() {
    setRead(null);
    setDraft(null);
    setFile(null);
    setShowText(false);
    if (fileInput.current) fileInput.current.value = "";
  }

  async function readFile() {
    if (!file) return;
    setBusy(true);
    setError(null);
    setNotice(null);

    const form = new FormData();
    form.set("file", file);
    // Sent only as a fallback: the slip's own digits pick the card when they
    // can be read.
    if (cardId) form.set("cardId", cardId);
    try {
      const res = await fetch("/api/slips/read", {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken() ?? ""}` },
        body: form,
      });
      const body = await res.json();
      if (!res.ok) {
        setError(body.error ?? "The slip could not be read");
        return;
      }
      const slip = body as SlipRead;
      setRead(slip);
      // Only a clean read of the printed digits selects a card. Anything less
      // is offered below as a question, because picking wrongly files the
      // spend against the wrong balance and the wrong earn rules.
      if (slip.suggestedCardId) setCardId(slip.suggestedCardId);
      setDraft({
        merchant: slip.merchant ?? "",
        amount: slip.amount != null ? String(slip.amount) : "",
        date: slip.date ?? todayInput(),
        category: slip.category,
        channel: slip.channel,
        isInternational: false,
        // An add-on card names its holder, so the slip knows whose spend it was.
        cardholderId: slip.suggestedCardholderId ?? "",
        notes: "",
      });
    } catch {
      setError("The upload failed. Check the server is running and try again.");
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (!read || !draft || !file || !cardId) return;
    const amount = Number(draft.amount);
    if (!draft.merchant.trim() || !Number.isFinite(amount) || amount <= 0 || !draft.date) {
      setError("A merchant, an amount and a date are needed before this can be saved.");
      return;
    }
    setBusy(true);
    setError(null);

    const form = new FormData();
    form.set("file", file);
    form.set("payload", JSON.stringify({
      // Whatever is selected now, which the slip may have changed.
      cardId,
      fileName: read.fileName,
      fileHash: read.fileHash,
      mimeType: read.mimeType,
      readBy: read.readBy,
      merchant: draft.merchant.trim(),
      amount,
      date: draft.date,
      category: draft.category,
      channel: draft.channel,
      isInternational: draft.isInternational,
      last4: read.last4,
      approvalCode: read.approvalCode,
      cardholderId: draft.cardholderId || null,
      notes: draft.notes.trim() || null,
      rawText: read.rawText,
      allowDuplicate: read.alreadyLogged,
    }));

    try {
      const res = await fetch("/api/slips", {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken() ?? ""}` },
        body: form,
      });
      const body = await res.json();
      if (!res.ok) {
        setError(body.error ?? "Could not save the slip");
        return;
      }
      setNotice(
        `Logged. ${formatPoints(body.transaction.pointsAwarded)} points earned via ${body.transaction.appliedRule}, pending until a statement confirms it.`,
      );
      reset();
      void load();
    } catch {
      setError("Could not save the slip");
    } finally {
      setBusy(false);
    }
  }

  async function remove(slip: ChargeSlipRow) {
    const willRemove = slip.verification === "PENDING";
    if (!confirm(
      willRemove
        ? `Delete this slip and the pending transaction it created?`
        : `Delete this slip? The statement has already confirmed the transaction, so that stays.`,
    )) return;
    try {
      const r = await api.del<{ removedTransaction: boolean }>(`/slips/${slip.id}`);
      setNotice(r.removedTransaction ? "Slip and its pending transaction removed." : "Slip removed.");
      void load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not delete that slip");
    }
  }

  const pending = (slips ?? []).filter((s) => s.verification === "PENDING");
  const pendingValue = pending.reduce((sum, s) => sum + (s.amount ?? 0), 0);
  const pendingPoints = pending.reduce((sum, s) => sum + s.pointsAwarded, 0);

  return (
    <div>
      <PageHeader
        title="Charge slips"
        subtitle="Log a purchase from its slip and the points count straight away. The statement confirms it later rather than adding it again."
      />

      {error && <ErrorBanner message={error} onDismiss={() => setError(null)} />}
      {notice && (
        <div className="mb-4 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
          {notice}
        </div>
      )}

      {pending.length > 0 && (
        <div className="mb-6 grid gap-4 sm:grid-cols-3">
          <StatTile label="Waiting on a statement" value={String(pending.length)} sub="Slips not yet confirmed" />
          <StatTile label="Spend not yet confirmed" value={formatINR(pendingValue)} />
          <StatTile label="Points already counted" value={formatPoints(pendingPoints)} sub="Included in your balances" />
        </div>
      )}

      {cards.filter((c) => c.isActive).length === 0 ? (
        <Panel title="Log a slip" className="mb-6">
          <EmptyState
            title="No cards to file a slip against"
            hint="Add a card first — a slip is filed against the card that paid, and its earn rules decide what the purchase is worth."
            action={<Link to="/cards" className="btn-primary">Add a card</Link>}
          />
        </Panel>
      ) : (
      <Panel title="Log a slip" className="mb-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="s-card">Which card was used?</label>
            <select id="s-card" className="input" value={cardId} onChange={(e) => setCardId(e.target.value)}>
              {cards.filter((c) => c.isActive).map((c) => (
                <option key={c.id} value={c.id}>{c.name}{c.last4 ? ` ···· ${c.last4}` : ""}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-ink-500">
              {read?.suggestedCardId
                ? read.suggestedVia === "ADD_ON"
                  ? "Chosen from the add-on card number printed on the slip."
                  : "Chosen from the card number printed on the slip."
                : "Usually picked from the slip itself. Set it here when the digits cannot be read."}
            </p>
          </div>
          <div>
            <label className="label" htmlFor="s-file">Photo of the slip</label>
            <input
              id="s-file"
              ref={fileInput}
              type="file"
              className="input"
              accept="image/*,.pdf"
              capture="environment"
              onChange={(e) => { setFile(e.target.files?.[0] ?? null); setRead(null); setDraft(null); }}
            />
            <p className="mt-1 text-xs text-ink-500">On a phone this opens the camera. A PDF the merchant emailed reads exactly.</p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button className="btn-primary" disabled={!file || busy} onClick={() => void readFile()}>
            {busy && !read ? "Reading…" : "Read the slip"}
          </button>
          {file && <span className="text-xs text-ink-500">{file.name}</span>}
        </div>
      </Panel>
      )}

      {busy && !read && <Spinner label="Reading the slip…" />}

      {read && draft && (
        <Panel
          title="Check before saving"
          subtitle={`Read by ${read.readBy === "OCR" ? "reading the photo" : read.readBy === "PDF_TEXT" ? "the PDF's own text" : "hand"}${read.approvalCode ? ` · approval ${read.approvalCode}` : ""}`}
          className="mb-6"
          actions={
            <div className="flex gap-2">
              <button className="btn-secondary" onClick={reset}>Discard</button>
              <button className="btn-primary" disabled={busy || !cardId} onClick={() => void save()}>
                {busy ? "Saving…" : "Log this purchase"}
              </button>
            </div>
          }
        >
          {read.cardCandidates.length > 0 && (
            <div className="mb-4 rounded-lg border border-ink-300 bg-ink-50 p-3">
              <h3 className="label mb-1">Which card was this?</h3>
              <p className="mb-3 text-xs text-ink-500">
                The number on the slip read as{" "}
                <span className="tabular-nums font-medium text-ink-700">{read.cardCandidates[0].reading}</span>,
                which is not exactly any of yours. These are the closest — or leave the card above as it is.
              </p>
              <div className="flex flex-wrap gap-2">
                {read.cardCandidates.map((candidate) => (
                  <button
                    key={candidate.cardId}
                    className={`chip-action rounded-lg border text-sm ${
                      cardId === candidate.cardId
                        ? "border-accent-500 bg-accent-50 text-accent-900"
                        : "border-ink-300 bg-white hover:bg-ink-50"
                    }`}
                    onClick={() => setCardId(candidate.cardId)}
                  >
                    <span className="font-medium">{candidate.cardName}</span>
                    <span className="ml-1.5 text-xs text-ink-500">
                      ···· {candidate.digits}
                      {candidate.via === "ADD_ON" ? " · add-on" : ""}
                      {candidate.distance === 1 ? " · 1 digit off" : ` · ${candidate.distance} digits off`}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {read.warnings.map((w) => (
            <p key={w} className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">{w}</p>
          ))}
          {read.notes.map((n) => (
            <p key={n} className="mb-3 text-sm text-ink-600">{n}</p>
          ))}

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="d-merchant">Merchant</label>
              <input id="d-merchant" className="input" value={draft.merchant} onChange={(e) => setDraft({ ...draft, merchant: e.target.value })} />
            </div>
            <div>
              <label className="label" htmlFor="d-amount">Amount (₹)</label>
              <input id="d-amount" className="input tnum" type="number" min={0} step="0.01" value={draft.amount} onChange={(e) => setDraft({ ...draft, amount: e.target.value })} />
            </div>
            <div>
              <label className="label" htmlFor="d-date">Date</label>
              <input id="d-date" className="input" type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} />
            </div>
            <div>
              <label className="label" htmlFor="d-category">Category</label>
              <select id="d-category" className="input" value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })}>
                {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="label" htmlFor="d-channel">Channel</label>
              <select id="d-channel" className="input" value={draft.channel} onChange={(e) => setDraft({ ...draft, channel: e.target.value as "ONLINE" | "OFFLINE" })}>
                <option value="OFFLINE">In store</option>
                <option value="ONLINE">Online</option>
              </select>
            </div>
            {people.length > 1 && (
              <div>
                <label className="label" htmlFor="d-who">Who spent it</label>
                <select id="d-who" className="input" value={draft.cardholderId} onChange={(e) => setDraft({ ...draft, cardholderId: e.target.value })}>
                  <option value="">Not recorded</option>
                  {people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
            )}
          </div>

          <label className="mt-4 flex items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-ink-300 text-accent-600 focus:ring-accent-500"
              checked={draft.isInternational}
              onChange={(e) => setDraft({ ...draft, isInternational: e.target.checked })}
            />
            International spend
          </label>

          {read.rawText && (
            <div className="mt-4">
              <button className="btn-ghost text-xs" onClick={() => setShowText((v) => !v)}>
                {showText ? "Hide" : "Show"} what was read off the slip
              </button>
              {showText && (
                <pre className="mt-2 max-h-60 overflow-auto whitespace-pre-wrap rounded-lg bg-ink-50 p-3 text-xs text-ink-600">
                  {read.rawText}
                </pre>
              )}
            </div>
          )}
        </Panel>
      )}

      <Panel title="Slips logged" subtitle="Pending means the statement has not shown it yet.">
        {slips === null ? (
          <Spinner />
        ) : slips.length === 0 ? (
          <EmptyState title="No slips yet" hint="Photograph one at the till and it lands here with its points already counted." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-200 text-left text-xs uppercase tracking-wide text-ink-500">
                  <th className="py-2 pr-3">Date</th>
                  <th className="py-2 pr-3">Merchant</th>
                  <th className="py-2 pr-3">Card</th>
                  <th className="py-2 pr-3 text-right">Amount</th>
                  <th className="py-2 pr-3 text-right">Points</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2"></th>
                </tr>
              </thead>
              <tbody>
                {slips.map((slip) => (
                  <tr key={slip.id} className="border-b border-ink-100">
                    <td className="py-2 pr-3 whitespace-nowrap tabular-nums text-ink-600">
                      {slip.date ? slip.date.slice(0, 10) : "—"}
                    </td>
                    <td className="min-w-0 py-2 pr-3">
                      <span className="block truncate font-medium text-ink-900" title={slip.merchant ?? ""}>
                        {slip.merchant ?? "—"}
                      </span>
                      {slip.approvalCode && (
                        <span className="text-xs text-ink-400">approval {slip.approvalCode}</span>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <span className="inline-flex items-center gap-2">
                        <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ background: slip.cardColor }} aria-hidden />
                        <span className="text-ink-700">{slip.cardName}</span>
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-right tabular-nums">{slip.amount != null ? formatINR(slip.amount) : "—"}</td>
                    <td className="py-2 pr-3 text-right tabular-nums text-ink-600">{formatPoints(slip.pointsAwarded)}</td>
                    <td className="py-2 pr-3">
                      {slip.verification === "CONFIRMED" ? (
                        <Badge tone="good">on the statement</Badge>
                      ) : slip.verification === "PENDING" ? (
                        <Badge tone="warning">pending</Badge>
                      ) : (
                        <Badge tone="neutral">no transaction</Badge>
                      )}
                    </td>
                    <td className="py-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {slip.hasImage && (
                          <a
                            className="btn-ghost text-xs"
                            href={`/api/slips/${slip.id}/image`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View
                          </a>
                        )}
                        <button className="btn-ghost text-xs text-rose-700" onClick={() => void remove(slip)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  );
}
